import { useMemo } from "react";
import type { PlanFeatureKey } from "@/core/hooks/planFeature";
import { useUserSector } from "@/core/hooks/useUserSector";
import { useAccountAccesses } from "@/hooks/useAccountAccesses";
import { capture } from "@/shared/lib/analytics";
import { useBillingSummary } from "./useBillingSummary";
import { usePlanChangeActions } from "./usePlanChangeActions";

/**
 * De onde veio o clique de upgrade (propriedade `surface` do evento): o
 * bloqueio de plano da rota e, desde a SA-1360 P4-C, o card, o aviso do
 * catálogo e o detalhe de Integrações.
 */
export type PlanGateSurface =
  | "plan_block"
  | "integrations_card"
  | "integrations_notice"
  | "integration_detail";

/** Rótulos do primário do bloqueio: dizem o que o clique faz (skill billing §7). */
export const PLAN_GATE_SELF_SERVICE_LABEL = "Ver planos e fazer upgrade";
export const PLAN_GATE_SALES_LABEL = "Falar com o comercial";

type UsePlanGateUpgradeInput = {
  feature: PlanFeatureKey;
  /** Nome da página, como no menu ("Disparos"): vai na mensagem de Vendas. */
  featureLabel: string;
  /** Plano mínimo da oferta; `null` quando não há plano a nomear. */
  requiredPlanName: string | null;
  /**
   * Só o admin da empresa vê o botão, e o resumo do billing é Admins-only:
   * desligado, nada é pedido (evita o 403 para quem não é admin).
   */
  enabled: boolean;
};

function buildSalesMessage(
  companyName: string,
  featureLabel: string,
  requiredPlanName: string | null
) {
  const who = companyName ? `Sou da conta ${companyName} e quero` : "Quero";
  // Sem plano nomeado (legado, topo da escada) a mensagem pede o recurso, sem
  // inventar plano: quem decide o plano do legado é o comercial.
  if (!requiredPlanName) return `${who} liberar ${featureLabel}.`;
  return `${who} fazer o upgrade para o plano ${requiredPlanName} para liberar ${featureLabel}.`;
}

/**
 * Primário do admin no bloqueio por plano (SA-1401). Roda a mesma ação do
 * bloco de uso da aba Faturamento (`usePlanChangeActions`):
 * - self-service (`can_self_upgrade` E plano mínimo nomeado): o Portal na
 *   troca de plano, com o rótulo "Ver planos e fazer upgrade";
 * - senão (conta sem assinatura no Stripe, plano legado, topo da escada,
 *   resumo que falhou): o WhatsApp de Vendas, com o rótulo "Falar com o
 *   comercial". O legado nunca vai ao Portal: lá o catálogo mostraria um
 *   plano "maior" mais barato do que ele paga (24/09/2026).
 *
 * `salesPath` leva o mesmo caminho ao corpo do bloqueio ("Fale com o nosso time
 * comercial"). `isPathPending` diz que o caminho ainda depende do resumo: o
 * bloqueio do admin espera por ele, para nem o rótulo nem o corpo trocarem na
 * frente da pessoa.
 *
 * Levar só até a aba deixava o admin de pouco uso sem ação nenhuma: lá o CTA
 * de upgrade só aparece perto do limite.
 */
export function usePlanGateUpgrade({
  feature,
  featureLabel,
  requiredPlanName,
  enabled,
}: UsePlanGateUpgradeInput) {
  const { activeSectorId } = useUserSector();
  const { data: accountAccesses = [] } = useAccountAccesses();
  const activeAccess = useMemo(
    () => accountAccesses.find((a) => a.sector.documentId === activeSectorId),
    [accountAccesses, activeSectorId]
  );
  const companyId = activeAccess?.company.documentId ?? null;
  const companyName = activeAccess?.company.name ?? "";

  // O resumo só decide algo quando há plano nomeado (Portal ou comercial);
  // sem ele o caminho é sempre o comercial, e nada é pedido.
  const needsSummary = enabled && requiredPlanName !== null;
  const { data: summary, isLoading: isSummaryLoading } = useBillingSummary(
    needsSummary ? companyId : null
  );
  const planChange = usePlanChangeActions(companyId);
  // Com plano nomeado, o caminho depende do resumo. Enquanto ele carrega, o
  // caminho é PENDENTE: quem mostra o bloqueio espera, em vez de exibir um
  // rótulo e um corpo e trocar os dois quando o resumo chega. Resumo que
  // falhou encerra a espera no comercial (fail-closed).
  const isPathPending = needsSummary && isSummaryLoading;
  const selfService = needsSummary && summary?.can_self_upgrade === true;

  const upgrade = async (surface: PlanGateSurface) => {
    const goToPortal = selfService;
    capture("billing_plan_gate_upgrade_clicked", {
      company_id: companyId,
      company_name: companyName,
      feature,
      required_plan: requiredPlanName,
      path: goToPortal ? "portal" : "sales",
      surface,
    });
    if (goToPortal) {
      await planChange.openPlanChange();
      return;
    }
    planChange.openSales(
      buildSalesMessage(companyName, featureLabel, requiredPlanName)
    );
  };

  return {
    upgrade,
    /** O caminho ainda depende do resumo: o bloqueio do admin espera. */
    isPathPending,
    /** O primário vai para o comercial (decide o corpo do bloqueio). */
    salesPath: !selfService,
    primaryLabel: selfService
      ? PLAN_GATE_SELF_SERVICE_LABEL
      : PLAN_GATE_SALES_LABEL,
    isLoading: enabled && (isPathPending || planChange.isOpeningPlanChange),
    error: planChange.planChangeError,
  };
}
